import React, { useContext, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import MyContext from "../MyContext/MyContext";

const ShopProduct = ({ grid, setGrid, sidebarController, filters }) => {
  const { data, loading, error } = useContext(MyContext);

  const { category } = useParams();

  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 6;

  // Flatten products + attach routing_category from parent
  const allProducts = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.flatMap((cat) =>
      (cat.products || []).map((p) => ({
        ...p,
        routing_category: cat.routing_category?.toLowerCase(),
      }))
    );
  }, [data]);

  // FILTERS
  const filteredProducts = useMemo(() => {
    return allProducts.filter((p) => {
      // FIXED: AUTO FILTER BY ROUTE CATEGORY
      if (category && p.routing_category !== category.toLowerCase()) return false;

      // EXISTING FILTERS (unchanged)
      if (filters.category && p.category !== filters.category) return false;
      if (p.price.current < filters.price[0] || p.price.current > filters.price[1]) return false;
      if (filters.rating && Math.floor(p.rating?.stars) < filters.rating) return false;
      if (filters.color && !p.colors?.includes(filters.color)) return false;
      if (filters.brand && p.details?.Brand !== filters.brand) return false;

      return true;
    });
  }, [allProducts, filters, category]);

  const indexOfLast = currentPage * productsPerPage;
  const indexOfFirst = indexOfLast - productsPerPage;

  const currentProducts = filteredProducts.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters, category]);

  if (loading) return <h2>Loading products...</h2>;
  if (error) return <h2>{error}</h2>;

  return (
    <div className="col-lg-9">

      {/* ---------- EMPTY STATE STYLE ---------- */}
      <style>{`
        .empty-box {
          padding: 60px 20px;
          text-align: center;
          background: #fff;
          border: 1px solid #eee;
          border-radius: 14px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.06);
          animation: fadeIn 0.4s ease forwards;
        }
        .empty-icon {
          font-size: 72px;
          color: #b30000;
          margin-bottom: 12px;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ---------- TOP BAR ---------- */}
      <div className="flex-between gap-16 flex-wrap mb-40">
        <span className="text-gray-900">
          Showing {filteredProducts.length === 0 ? 0 : indexOfFirst + 1}-
          {Math.min(indexOfLast, filteredProducts.length)} of {filteredProducts.length} products
        </span>

        <div className="flex-align gap-16 flex-wrap">
          <div className="list-grid-btns flex-align gap-16">
            <button
              onClick={() => setGrid(true)}
              type="button"
              className={`w-44 h-44 flex-center border rounded-6 text-2xl border-gray-100 ${
                grid === true && "border-main-600 text-white bg-main-600"
              }`}
              style={{ transition: "0.3s" }}
            >
              <i className="ph-bold ph-list-dashes" />
            </button>

            <button
              onClick={() => setGrid(false)}
              type="button"
              className={`w-44 h-44 flex-center border rounded-6 text-2xl border-gray-100 ${
                grid === false && "border-main-600 text-white bg-main-600"
              }`}
              style={{ transition: "0.3s" }}
            >
              <i className="ph ph-squares-four" />
            </button>
          </div>

          <button
            onClick={sidebarController}
            type="button"
            className="w-44 h-44 d-lg-none d-flex flex-center border border-gray-100 rounded-6 text-2xl"
            style={{
              transition: "0.3s",
              background: "white",
            }}
          >
            <i className="ph-bold ph-funnel" />
          </button>
        </div>
      </div>

      {/* EMPTY STATE */}
      {filteredProducts.length === 0 && (
        <div className="empty-box">
          <div className="empty-icon">
            <i className="ph-bold ph-warning-circle" />
          </div>

          <h3 style={{ fontWeight: 700, marginBottom: "10px" }}>
            No Products Found
          </h3>

          <p style={{ color: "#666", fontSize: "16px", marginBottom: "22px" }}>
            Try adjusting your filters or explore other categories.
          </p>

          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "12px 22px",
              borderRadius: "8px",
              background: "#b30000",
              color: "white",
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
              transition: "0.3s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#7a0000")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#b30000")}
          >
            Reset Filters
          </button>
        </div>
      )}

      {/* PRODUCT CARDS */}
      {filteredProducts.length > 0 && (
        <>
          <div className={`list-grid-wrapper ${grid && "list-view"}`}>
            {currentProducts.map((product, index) => (
              <div
                key={product.id}
                className="product-card h-100 p-16 border border-gray-100 rounded-16 position-relative transition-2"
                style={{
                  background: "white",
                  transition: "0.3s",
                }}
              >
                <Link
to={`/shop/${product.routing_category}/${product.id}`}
                  state={{ product }}
                  className="product-card__thumb flex-center rounded-8 position-relative"
                  style={{ background: "white" }}
                >
                  {index === 0 && (
                    <span className="product-card__badge bg-primary-600 px-8 py-4 text-sm text-white position-absolute inset-inline-start-0 inset-block-start-0">
                      Best Sales
                    </span>
                  )}

                  <img
                    src={product.image}
                    alt={product.title}
                    className="product-img-fix"
                    style={{ transition: "0.3s" }}
                  />
                </Link>

                <div className="product-card__content mt-16">
                  <h6 className="title text-lg fw-semibold mt-12 mb-8">
                    <Link
                      to={`/product-details/${product.id}`}
                      state={{ product }}
                      className="link text-line-2"
                    >
                      {product.title}
                    </Link>
                  </h6>

                  <div className="flex-align mb-20 mt-16 gap-6">
                    <span className="text-xs fw-medium text-gray-500">
                      {product.rating?.stars}
                    </span>
                    <span className="text-15 fw-medium text-warning-600 d-flex">
                      <i className="ph-fill ph-star" />
                    </span>
                    <span className="text-xs fw-medium text-gray-500">
                      ({product.rating?.reviews_count})
                    </span>
                  </div>

                  <div className="product-card__price my-20">
                    <span className="text-gray-400 text-md fw-semibold text-decoration-line-through">
                      ${product.price?.original}
                    </span>
                    <span className="text-heading text-md fw-semibold">
                      ${product.price?.current}
                      <span className="text-gray-500 fw-normal"> /Qty</span>
                    </span>
                  </div>

                  <Link
                    to="/cart"
                    className="product-card__cart py-11 px-24 rounded-8 flex-center gap-8 fw-medium"
                    style={{
                      background: "white",
                      border: "1px solid #ddd",
                      color: "black",
                      transition: "0.3s",
                    }}
                  >
                    Add To Cart <i className="ph ph-shopping-cart" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <ul className="pagination flex-center flex-wrap gap-16 mt-40">
            <li className="page-item">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                className="page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium border border-gray-100"
                style={{ background: "white", transition: "0.3s" }}
              >
                <i className="ph-bold ph-arrow-left" />
              </button>
            </li>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <li key={page} className={`page-item ${page === currentPage ? "active" : ""}`}>
                <button
                  onClick={() => handlePageChange(page)}
                  className="page-link h-64 w-64 flex-center text-md rounded-8 fw-medium border border-gray-100"
                  style={{ background: "white", transition: "0.3s" }}
                >
                  {page.toString().padStart(2, "0")}
                </button>
              </li>
            ))}

            <li className="page-item">
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                className="page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium border border-gray-100"
                style={{ background: "white", transition: "0.3s" }}
              >
                <i className="ph-bold ph-arrow-right" />
              </button>
            </li>
          </ul>
        </>
      )}
    </div>
  );
};

export default ShopProduct;
